import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTimeOnTask } from "@/hooks/useTimeOnTask";
import { api, errMsg } from "@/lib/api";
import { Loading, scoreTone } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, CheckCircle2, XCircle, Info, RotateCcw, MessageCircleQuestion, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function QuizPage() {
  const { id } = useParams();
  const [lesson, setLesson] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [i, setI] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  useTimeOnTask("quiz", id);

  useEffect(() => {
    api.get(`/lessons/${id}`).then(({ data }) => {
      setLesson(data);
      setAnswers(new Array(data.quiz.length).fill(null));
    }).catch((e) => { toast.error(errMsg(e)); navigate("/catalog"); });
  }, [id, navigate]);

  if (!lesson) return <Loading />;
  const quiz = lesson.quiz;
  if (!quiz.length) return <div className="text-muted-foreground">This lesson has no quiz.</div>;
  const q = quiz[i];
  const answered = answers.filter((a) => a !== null).length;

  const submit = async () => {
    if (answers.some((a) => a === null)) {
      const first = answers.findIndex((a) => a === null);
      toast.error("Answer every question before submitting");
      setI(first);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/lessons/${id}/attempts`, { answers });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const retake = () => {
    setResult(null);
    setAnswers(new Array(quiz.length).fill(null));
    setI(0);
  };

  if (result) {
    return (
      <div className="max-w-[820px]">
        <div className="bg-white border border-border rounded-[18px] p-6 sm:p-8 fade-up" data-testid="quiz-result">
          <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground">Quiz results · {lesson.title}</div>
          <div className="mt-3 flex flex-wrap items-end gap-4">
            <div className={`text-6xl font-display font-semibold ${scoreTone(result.percent)}`} data-testid="quiz-score-percent">{result.percent}%</div>
            <div className="pb-2 text-muted-foreground">{result.correct} of {result.total} correct</div>
            {result.xp_gained > 0 && <div className="pb-2 ml-auto flex items-center gap-1 text-[#7A5A00] bg-[#FFF1C7] rounded-full px-3 py-1 text-sm font-medium" data-testid="quiz-xp-gained"><Star className="h-4 w-4" />+{result.xp_gained} XP</div>}
          </div>
          <p className="mt-3 text-[#123645]">{result.percent >= 80 ? "Excellent work! You really understand this." : result.percent >= 60 ? "Nice job — review the ones you missed below." : "Keep going! Read the explanations, ask Sage, and try again."}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={retake} variant="outline" className="bg-white" data-testid="quiz-retake-button"><RotateCcw className="h-4 w-4 mr-2" />Retake quiz</Button>
            <Button asChild variant="outline" className="bg-white" data-testid="quiz-ask-tutor-button"><Link to={`/lessons/${id}`}><MessageCircleQuestion className="h-4 w-4 mr-2" />Review lesson with Sage</Link></Button>
            {lesson.course && <Button asChild data-testid="quiz-back-course-button"><Link to={`/courses/${lesson.course.course_id}`}>Back to course</Link></Button>}
          </div>
        </div>
        <div className="mt-6 space-y-4">
          {quiz.map((qq, k) => {
            const r = result.results[k];
            return (
              <div key={k} className="bg-white border border-border rounded-[14px] p-5" data-testid="quiz-review-item">
                <div className="flex gap-2 items-start">
                  {r.correct ? <CheckCircle2 className="h-5 w-5 text-[#1F9D55] shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-[#D64545] shrink-0 mt-0.5" />}
                  <div className="font-medium">{k + 1}. {qq.question}</div>
                </div>
                <div className="mt-3 grid gap-2">
                  {qq.options.map((o, oi) => {
                    const isAns = oi === r.answer_index;
                    const isSel = oi === r.selected;
                    const cls = isAns ? "border-[#1F9D55]/40 bg-[#DDF7E7]" : isSel ? "border-[#F26B6B]/40 bg-[#FFE1E1]" : "border-border bg-[#FBFAF4]";
                    return (
                      <div key={oi} className={`border rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${cls}`}>
                        {isAns ? <CheckCircle2 className="h-4 w-4 text-[#1F9D55]" /> : isSel ? <XCircle className="h-4 w-4 text-[#D64545]" /> : <span className="w-4" />}
                        {o}
                        {isAns && <span className="ml-auto text-xs text-[#14663A]">Correct answer</span>}
                        {isSel && !isAns && <span className="ml-auto text-xs text-[#9B2C2C]">Your answer</span>}
                      </div>
                    );
                  })}
                </div>
                {r.explanation && (
                  <div className="mt-3 bg-[#F5F1E6] rounded-lg p-3 text-sm text-[#123645] flex gap-2" data-testid="quiz-explanation">
                    <Info className="h-4 w-4 shrink-0 mt-0.5 text-[#0F7B7D]" />
                    <div><span className="font-semibold">{r.correct ? "Nice work — " : "Not quite — "}</span>{r.explanation}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[820px]">
      <Button variant="ghost" asChild className="mb-4 -ml-3"><Link to={`/lessons/${id}`}><ArrowLeft className="h-4 w-4 mr-1" />Back to lesson</Link></Button>
      <div className="sticky top-14 lg:top-0 z-20 bg-[#FBFAF4] py-3">
        <div className="flex justify-between text-sm text-muted-foreground mb-2">
          <span data-testid="quiz-question-count">Question {i + 1} of {quiz.length}</span>
          <span>{answered} answered</span>
        </div>
        <Progress value={((i + 1) * 100) / quiz.length} className="h-2" data-testid="quiz-progress" />
      </div>
      <div className="bg-white border border-border rounded-[18px] p-6 sm:p-8 mt-4 fade-up" key={i}>
        <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground">{lesson.title}</div>
        <h2 className="mt-3 text-xl sm:text-2xl font-display font-semibold text-[#071A22]" data-testid="quiz-question-text">{q.question}</h2>
        <div className="mt-6 grid gap-3" role="radiogroup">
          {q.options.map((o, oi) => {
            const sel = answers[i] === oi;
            return (
              <button
                key={oi}
                role="radio"
                aria-checked={sel}
                onClick={() => setAnswers(answers.map((a, k) => (k === i ? oi : a)))}
                className={`text-left min-h-[56px] rounded-xl border px-4 py-3 flex items-center gap-3 transition-colors duration-150 ${
                  sel ? "border-[#0F7B7D] bg-[#D7F3F1] ring-2 ring-[#0F7B7D]/20" : "border-border bg-white hover:bg-[#F5F1E6]"
                }`}
                data-testid="quiz-option"
              >
                <span className={`h-7 w-7 shrink-0 rounded-full border flex items-center justify-center text-sm font-semibold ${sel ? "bg-[#0F7B7D] text-white border-[#0F7B7D]" : "border-border"}`}>{String.fromCharCode(65 + oi)}</span>
                <span>{o}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-8 flex justify-between gap-3">
          <Button variant="outline" className="bg-white h-11" disabled={i === 0} onClick={() => setI(i - 1)} data-testid="quiz-prev-button">Previous</Button>
          {i < quiz.length - 1 ? (
            <Button className="h-11" onClick={() => setI(i + 1)} disabled={answers[i] === null} data-testid="quiz-next-button">Next question</Button>
          ) : (
            <Button className="h-11" onClick={submit} disabled={busy} data-testid="quiz-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Submit quiz</Button>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2 justify-center">
        {quiz.map((_, k) => (
          <button key={k} onClick={() => setI(k)} aria-label={`Go to question ${k + 1}`} className={`h-9 w-9 rounded-full text-sm font-medium border ${k === i ? "bg-[#0F7B7D] text-white border-[#0F7B7D]" : answers[k] !== null ? "bg-[#D7F3F1] border-[#0F7B7D]/30" : "bg-white border-border"}`} data-testid="quiz-jump-button">{k + 1}</button>
        ))}
      </div>
    </div>
  );
}
